import React, { useRef, useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { formatNumberWithSymbols } from "../../../helperJs/helper";
import ChartImage from "../../Assets/home/chart-3.png";
import { useTranslation } from "react-i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

const LineChart = ({ data }) => {
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(data);
  const [loader, setLoader] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const ctx = chart.ctx;
    // 🎨 Create gradients for each dataset
    const gradients = [
      {
        top: "rgba(224, 224, 252, .7)",
        bottom: "rgba(224, 224, 252, .5)",
      },
      {
        top: "rgba(255, 207, 215, .7)",
        bottom: "rgba(255, 207, 215, .5)",
      },
    ];
    const updatedDatasets = data.datasets.map((dataset, i) => {
      const gradient = ctx.createLinearGradient(0, 0, 0, chart.height);
      gradient.addColorStop(0, gradients[i % gradients.length].top);
      gradient.addColorStop(1, gradients[i % gradients.length].bottom);

      return {
        ...dataset,
        fill: true,
        tension: 0.4,
        backgroundColor: gradient,
      };
    });

    setChartData({
      ...data,
      datasets: updatedDatasets,
    });
    setLoader(false);
  }, [data]);

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "top",
        display: false,
      },
      title: {
        display: true,
      },
      axisY: {
        gridDashType: "dash",
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";

            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += formatNumberWithSymbols(context.parsed.y?.toFixed(0));
            }
            return label;
          },
        },
        boxPadding: 5,
        cornerRadius: 1,
        caretSize: 10,
        padding: 10,
        margin: 0,
        boxHeight: 30,
        borderColor: "rgba(0, 0, 0, 0.2)",
        borderWidth: 1,
        borderRadius: 5,
        boxWidth: 10,
        titleColor: "black",
        bodyColor: "black",
        bodyFont: {
          size: 14,
          weight: 500,
          font: "Lato",
        },
        bodySpacing: 7,
        usePointStyle: true,
        backgroundColor: "#FFFFFF",
        titleFont: {
          size: 14,
          weight: 400,
          font: "Lato",
        },
      },
    },
    scales: {
      y: {
        type: "linear",
        display: true,
        position: "left",
        reverse: false,
        beginAtZero: true,
        color: "black",
        ticks: {
          maxTicksLimit: 7,
          stepSize: 6000,
          autoSkip: false,
          beginAtZero: true,
          size: 16,
          weight: 500,
          callback: function (value) {
            const number = formatNumberWithSymbols(value);
            return number;
          },
        },
        grid: {
          display: true,
          tickBorderDash: [2, 15], // also for the tick, if long enough
          tickLength: 10, // just to see the dotted line
          tickWidth: 2,
        },
        title: {
          display: data?.title?.[0]?.left ? true : false,
          text: data?.title?.[0]?.left,
          color: data?.title?.[0]?.color,
          font: {
            size: 14,
            weight: 600,
            family: "Lato",
          },
        },
      },

      x: {
        ticks: {
          display: true,
          color: "#666",
          callback: function (value, index) {
            return index % 2 === 0 ? this.getLabelForValue(value) : null;
          },
        },
        grid: {
          display: false,
        },
      },
    },
    cubicInterpolationMode: "monotone",
  };

  return (
    <div style={{ height: "100%", minWidth: "100%" }}>
      {data?.datasets?.length > 0 ? (
        <Line
          options={options}
          ref={chartRef}
          data={loader ? data : chartData}
        />
      ) : (
        <>
          <img
            src={ChartImage}
            alt="no-chart"
            className="w-100 h-100 blur-card-wth-btn rounded-4"
          />
          <div className="blur-card-wth-btn">
            <span>{t("common_buttons.no_data")}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default LineChart;
